import { humanize } from 'inflected';
import { ScenarioPlanningModelType } from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import { CHANNEL_NAME_MAP } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { toFloat } from '@src/utils/data';
import { PLAN_OPTIONS } from '@Pages/GeoMMM/ScenarioPlanning/Modals/AdjustModal';
import { normalizeWeightsByAverage } from '@Pages/HROAS/utils/hroasUtils';
import { Slot } from '@Pages/HROAS/utils/slots';
import {
  extractPairwiseData,
  PairwiseInteraction
} from '@Pages/GeoMMM/ScenarioPlanning/RMMMScenarioPlanningUtils';
import {
  analyzeBrandEquityScenarioLambda,
  analyzeGeoMMMScenarioLambda,
  AnalyzeResultRow,
  analyzeHROASScenarioLambda,
  analyzeRMMMScenarioLambda,
  AnalyzeScenarioResult,
  BaselineData,
  BaselineSpendRow,
  CometProgress,
  HROASRunArgs,
  ModelConfig,
  ScenarioLambdaInput,
  ScenarioPlanningModelExt
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';

export { GX_TRACES_S3_KEYS } from '@Pages/GeoMMM/ScenarioPlanning/RMMMScenarioPlanningUtils';
export type { PairwiseInteraction } from '@Pages/GeoMMM/ScenarioPlanning/RMMMScenarioPlanningUtils';
export type { ModelConfig } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';

// Runtime-flippable diagnostic flag. Set `window.__spDebug = true` in DevTools
// to surface `[rmmm-debug]` channel-rollup traces; zero-impact when false.
export const isSpDebug = (): boolean =>
  typeof window !== 'undefined' &&
  (window as unknown as { __spDebug?: boolean }).__spDebug === true;

export const MODEL_TYPES = {
  BRAND_EQUITY: 'BrandEquity',
  GEO_MMM: 'GeoMMM',
  R_MMM: 'RMMM',
  // ==== HROAS (unmigrated): holistic orchestrator model type — see src/Pages/HROAS/ ====
  HROAS: 'HROAS'
} as const;
export type MODEL_TYPES = (typeof MODEL_TYPES)[keyof typeof MODEL_TYPES];

// ==== HROAS (unmigrated): the lambda tags rows with modelType 'GeoMMM'|'BrandEquity'|'rMMM'
// (note the lowercase 'rMMM' vs the MODEL_TYPES.R_MMM enum value 'RMMM'). These helpers map a
// raw per-row modelType to the enum and detect rMMM-type rows (which carry direct+indirect). ====
export const ROW_MODEL_TYPE_TO_ENUM: Record<string, MODEL_TYPES> = {
  GeoMMM: MODEL_TYPES.GEO_MMM,
  BrandEquity: MODEL_TYPES.BRAND_EQUITY,
  rMMM: MODEL_TYPES.R_MMM
};

// ==== HROAS: Brand Equity's per-channel revenue is entirely brand-metric-mediated
// (stageOneShare × stage-two revenue in brandEquity.py) — there is no unmediated
// "direct-to-purchase" term in the model. The API still calls the field `directRevenue` (a
// schema convention shared across all three model types), so without this reclassification
// 100% of Brand Equity's revenue would render as Direct in the HROAS stage lens. Gated on
// isHroas (not modelType alone) — the standalone single-model Brand Equity page has no stage
// lens at all and must be completely unaffected. Applied once, at both row-construction sites
// (computeBaselineData, mapRowToScenarioDateRow) — every downstream consumer (byModel
// breakdown, showActualized toggle, Change Impact by-model panel/columns) inherits it for free. ====
export const reclassifyBrandEquityRevenueAsIndirect = <
  T extends {
    modelType?: string;
    directRevenue: number;
    directRevenueCiLo?: number;
    directRevenueCiHi?: number;
    indirectRevenue?: number;
    indirectRevenueCiLo?: number;
    indirectRevenueCiHi?: number;
  }
>(
  row: T,
  isHroas: boolean
): T =>
  isHroas && ROW_MODEL_TYPE_TO_ENUM[row.modelType ?? ''] === MODEL_TYPES.BRAND_EQUITY
    ? {
        ...row,
        directRevenue: 0,
        directRevenueCiLo: 0,
        directRevenueCiHi: 0,
        indirectRevenue: row.directRevenue,
        indirectRevenueCiLo: row.directRevenueCiLo,
        indirectRevenueCiHi: row.directRevenueCiHi
      }
    : row;

export const SCENARIO_METRIC_ENUM = {
  CPX: 'cpx',
  KPI_VOLUME: 'volume',
  REVENUE: 'revenue',
  ROAS: 'roas',
  SPEND: 'spend'
} as const;
export type SCENARIO_METRIC_ENUM = (typeof SCENARIO_METRIC_ENUM)[keyof typeof SCENARIO_METRIC_ENUM];

const SCENARIO_METRIC_PRETTY_ENUM = {
  CPX: 'CPX',
  KPI_VOLUME: 'KPI Volume',
  REVENUE: 'Revenue',
  ROAS: 'ROAS',
  SPEND: 'Spend'
} as const;
type SCENARIO_METRIC_PRETTY_ENUM =
  (typeof SCENARIO_METRIC_PRETTY_ENUM)[keyof typeof SCENARIO_METRIC_PRETTY_ENUM];

export const CHART_SORT_BY_ENUM = {
  // "Abc" is the single alphabetical (channel/platform/tactic name) sort; per-dimension
  // Channel/Platform/Tactic sorts were removed as redundant with it.
  ALPHABETICALLY: 'Abc',
  REVENUE: 'Revenue',
  REVENUE_WATERFALL: 'Revenue Waterfall',
  ROAS: 'ROAS',
  SPEND: 'Spend'
} as const;
export type CHART_SORT_BY_ENUM = (typeof CHART_SORT_BY_ENUM)[keyof typeof CHART_SORT_BY_ENUM];

export const SORT_BY_ENUM = {
  ALPHABETICALLY: 'Abc',
  SPEND: 'Spend',
  BASELINE_SPEND: 'BAU Spend',
  BASELINE_REVENUE: 'BAU Revenue',
  BASELINE_ROAS: 'BAU ROAS'
} as const;
export type SORT_BY_ENUM = (typeof SORT_BY_ENUM)[keyof typeof SORT_BY_ENUM];

export const SORT_DIRECTION_ENUM = {
  HIGH_TO_LOW: '↓',
  LOW_TO_HIGH: '↑'
} as const;
export type SORT_DIRECTION_ENUM = (typeof SORT_DIRECTION_ENUM)[keyof typeof SORT_DIRECTION_ENUM];

export const GROUP_BY_ENUM = {
  CHANNEL: 'Channel',
  PLATFORM: 'Channel > Platform',
  TACTIC: 'Channel > Platform > Tactic'
} as const;
export type GROUP_BY_ENUM = (typeof GROUP_BY_ENUM)[keyof typeof GROUP_BY_ENUM];

export const ScenarioGranularityEnum = {
  CHANNEL: 'channel',
  PLATFORM: 'platform',
  TACTIC: 'tactic'
} as const;
export type ScenarioGranularityEnum =
  (typeof ScenarioGranularityEnum)[keyof typeof ScenarioGranularityEnum];

export const DATA_KEY_SEPERATOR = '|__|';

export const SCENARIO_METRIC_PRETTY_NAME_MAP = {
  [SCENARIO_METRIC_ENUM.CPX]: SCENARIO_METRIC_PRETTY_ENUM.CPX,
  [SCENARIO_METRIC_ENUM.KPI_VOLUME]: SCENARIO_METRIC_PRETTY_ENUM.KPI_VOLUME,
  [SCENARIO_METRIC_ENUM.REVENUE]: SCENARIO_METRIC_PRETTY_ENUM.REVENUE,
  [SCENARIO_METRIC_ENUM.ROAS]: SCENARIO_METRIC_PRETTY_ENUM.ROAS,
  [SCENARIO_METRIC_ENUM.SPEND]: SCENARIO_METRIC_PRETTY_ENUM.SPEND
};

export const getEffectLabel = (outcomeVariable?: string): string => {
  if (!outcomeVariable) {
    return SCENARIO_METRIC_PRETTY_ENUM.REVENUE;
  }
  return outcomeVariable.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export const getCostMetricLabel = (outcomeVariable?: string): string => {
  const effect = getEffectLabel(outcomeVariable);
  if (effect === SCENARIO_METRIC_PRETTY_ENUM.REVENUE) {
    return SCENARIO_METRIC_PRETTY_ENUM.ROAS;
  }
  const singular = effect.endsWith('s') ? effect.slice(0, -1) : effect;
  return `Cost Per ${singular}`;
};

export const getScenarioMetricLabel = (
  metric: SCENARIO_METRIC_ENUM,
  outcomeVariable?: string
): string => {
  switch (metric) {
    case SCENARIO_METRIC_ENUM.REVENUE:
    case SCENARIO_METRIC_ENUM.KPI_VOLUME:
      return getEffectLabel(outcomeVariable);
    case SCENARIO_METRIC_ENUM.ROAS:
    case SCENARIO_METRIC_ENUM.CPX:
      return getCostMetricLabel(outcomeVariable);
    default:
      return SCENARIO_METRIC_PRETTY_NAME_MAP[metric];
  }
};

export const getChartSortOptionLabel = (
  option: CHART_SORT_BY_ENUM,
  outcomeVariable?: string
): string => {
  switch (option) {
    case CHART_SORT_BY_ENUM.REVENUE:
      return getEffectLabel(outcomeVariable);
    case CHART_SORT_BY_ENUM.REVENUE_WATERFALL:
      return `${getEffectLabel(outcomeVariable)} Waterfall`;
    case CHART_SORT_BY_ENUM.ROAS:
      return getCostMetricLabel(outcomeVariable);
    default:
      return option;
  }
};

export const getEditSortOptionLabel = (option: SORT_BY_ENUM, outcomeVariable?: string): string => {
  switch (option) {
    case SORT_BY_ENUM.BASELINE_REVENUE:
      return `BAU ${getEffectLabel(outcomeVariable)}`;
    case SORT_BY_ENUM.BASELINE_ROAS:
      return `BAU ${getCostMetricLabel(outcomeVariable)}`;
    default:
      return option;
  }
};

export const CHART_SORT_BY_OPTIONS = Object.values(CHART_SORT_BY_ENUM);
export const SORT_OPTIONS = Object.values(SORT_BY_ENUM);
export const SORT_DIRECTION_OPTIONS = Object.values(SORT_DIRECTION_ENUM);
export const SORT_DIRECTION_LABEL_MAP: Record<SORT_DIRECTION_ENUM, string> = {
  [SORT_DIRECTION_ENUM.HIGH_TO_LOW]: 'High to Low',
  [SORT_DIRECTION_ENUM.LOW_TO_HIGH]: 'Low to High'
};
export const GROUP_BY_OPTIONS = [
  GROUP_BY_ENUM.CHANNEL,
  GROUP_BY_ENUM.PLATFORM,
  GROUP_BY_ENUM.TACTIC
];
export const DIMENSIONS = Object.values(ScenarioGranularityEnum);

export const GROUP_BY_EXPAND_DEPTH_MAP: Record<GROUP_BY_ENUM, number> = {
  [GROUP_BY_ENUM.CHANNEL]: 0,
  [GROUP_BY_ENUM.PLATFORM]: 1,
  [GROUP_BY_ENUM.TACTIC]: 2
};

export interface ScenarioDateRow {
  [ScenarioGranularityEnum.CHANNEL]: string;
  [ScenarioGranularityEnum.PLATFORM]: string;
  [ScenarioGranularityEnum.TACTIC]: string;
  // The model's native media_channel coordinate (e.g. 'featid334', or 'tv, natl, disp' for
  // non-featid-masked clients) — the ONLY value the backend's scenario_spend override matches
  // against. scenarioRedshiftDataToTree uses this directly for the analyze key (refIds); it must
  // never be derived from channel/platform/tactic, since those are display fields that can be
  // resolved to human-readable names independently of what the model was actually trained with.
  feature: string;
  // Original channel/platform/tactic before pretty-name replacement (present only on transformed
  // rows). Kept for backward-compat fallback only — scenarioRedshiftDataToTree prefers `feature`.
  rawChannel?: string;
  rawPlatform?: string;
  rawTactic?: string;
  date: string;
  // ==== HROAS (unmigrated): per-instance id/label + the raw per-row model kind
  // ('GeoMMM'|'BrandEquity'|'rMMM') so the chart can color/group by model and show
  // direct/indirect only for rMMM-type instances within a mixed holistic view. ====
  modelId?: string;
  modelLabel?: string;
  modelType?: string;
  // ==== HROAS: per-model revenue breakdown, populated on a row once processScenarioResult
  // blends 2+ instances sharing a feature (which otherwise clears modelId/modelLabel above,
  // since a blended row no longer belongs to a single model). Lets scenarioRedshiftDataToTree
  // preserve by-model attribution for the Change Impact "by-model contribution" view even
  // through a blend. Absent on single-model rows — use modelId there instead. ====
  byModel?: ModelRevenueBreakdown;
  isDemandCapture?: boolean;
  // Direct (all models)
  directSpend: number;
  directRevenue: number;
  directRevenueCiLo?: number;
  directRevenueCiHi?: number;
  // Indirect (rMMM only)
  indirectSpend?: number;
  indirectRevenue?: number;
  indirectRevenueCiLo?: number;
  indirectRevenueCiHi?: number;
  // Reattributed out (rMMM only — amount given away by a demand-capture channel; zero-sum with
  // indirect* across a scenario's channels): actualizedRevenue = directRevenue + indirectRevenue -
  // reattributedOutRevenue.
  reattributedOutSpend?: number;
  reattributedOutRevenue?: number;
  reattributedOutRevenueCiLo?: number;
  reattributedOutRevenueCiHi?: number;
  // Actualized CI (rMMM only — joint posterior, not derivable)
  actualizedRevenueCiLo?: number;
  actualizedRevenueCiHi?: number;
}

// ==== HROAS: normalize raw per-model weights so only their PROPORTIONS matter — weights of
// 1·1·1·1 give the same result as 2·2·2·2. Scales each included model's weight so
// the included set's weights always average to 1 — a uniform weight change across every included
// model is therefore a no-op, and only relative differences between models move the total. Set+
// Record shaped for this file's callers; delegates its actual math to the shape-agnostic
// normalizeWeightsByAverage (shared with HROAS/utils/hroasUtils.ts's calculateNormalizedWeights,
// which does the same "divide by the average" over a Map<string, number> shape instead). ====
export const normalizeModelWeights = (
  modelWeights: Record<string, number>,
  includedModelIds: Set<string>
): Record<string, number> => {
  const ids = Array.from(includedModelIds);
  return normalizeWeightsByAverage(ids.map(id => ({ id, weight: modelWeights[id] ?? 1 })));
};

// ==== HROAS: revenue-only blending weight for a row, keyed by its owning model instance
// (row.modelId). Spend is never weighted/excluded — it's the actual dollar spent, not
// model-specific — so callers must apply this to revenue fields only. Expects an
// already-normalized weight map (see normalizeModelWeights above), not raw weights. Returns 1
// (a no-op) whenever either map is undefined, which is always true for non-HROAS pages, so this
// has zero effect on GeoMMM/rMMM/BrandEquity aggregation. ====
export const getModelRevenueWeight = (
  row: { modelId?: string },
  includedModelIds?: Set<string>,
  normalizedModelWeights?: Record<string, number>
): number => {
  if (!includedModelIds || !normalizedModelWeights) {
    return 1;
  }
  if (!row.modelId || !includedModelIds.has(row.modelId)) {
    return 0;
  }
  return normalizedModelWeights[row.modelId] ?? 1;
};

export type ModelRevenueBreakdown = Record<
  string,
  { directRevenue: number; indirectRevenue?: number; reattributedOutRevenue?: number }
>;

// ==== HROAS: sum one model-revenue-breakdown map's `field`, restricted to included models and
// scaled by each model's normalized weight. Used to recompute Change Impact / Edit Scenario
// comparison values reactively from ScenarioNode.baselineByModel/previousByModel — those trees
// are built once (unweighted) and never rebuilt on a toggle/weight change, so consumers must
// re-derive weighted totals at read time instead of reading node.previousDirectRevenue/
// baselineDirectRevenue directly. ====
export const sumWeightedByModel = (
  byModel: ModelRevenueBreakdown | undefined,
  includedModelIds: Set<string>,
  normalizedModelWeights: Record<string, number>,
  field: keyof ModelRevenueBreakdown[string]
): number => {
  let total = 0;
  for (const [modelId, contribution] of Object.entries(byModel ?? {})) {
    if (!includedModelIds.has(modelId)) {
      continue;
    }
    total += (normalizedModelWeights[modelId] ?? 1) * (contribution[field] ?? 0);
  }
  return total;
};

// ==== HROAS: like sumWeightedByModel, but sums direct + indirect - reattributedOut together —
// for consumers (e.g. EditScenarioModal's single "Revenue" column) that show one combined total
// rather than a stage-lens split. Needed because a model's whole revenue can live entirely in one
// field (e.g. Brand Equity's is 100% indirect post-reclassification) — summing only
// 'directRevenue' would silently drop that model's contribution. See getActualizedMetricValue for
// why reattributedOut is subtracted. ====
export const sumWeightedTotalByModel = (
  byModel: ModelRevenueBreakdown | undefined,
  includedModelIds: Set<string>,
  normalizedModelWeights: Record<string, number>
): number =>
  sumWeightedByModel(byModel, includedModelIds, normalizedModelWeights, 'directRevenue') +
  sumWeightedByModel(byModel, includedModelIds, normalizedModelWeights, 'indirectRevenue') -
  sumWeightedByModel(byModel, includedModelIds, normalizedModelWeights, 'reattributedOutRevenue');

// ==== Scale every model's direct/indirect/reattributedOut contribution by the same factor — used
// to derive the constant-ROAS live-edit estimate (currentByModel) from baselineByModel as spend
// changes. ====
export const scaleModelRevenueBreakdown = (
  byModel: ModelRevenueBreakdown | undefined,
  factor: number
): ModelRevenueBreakdown | undefined =>
  byModel &&
  Object.fromEntries(
    Object.entries(byModel).map(([modelId, contribution]) => [
      modelId,
      {
        directRevenue: Math.max(0, contribution.directRevenue * factor),
        indirectRevenue: Math.max(0, (contribution.indirectRevenue ?? 0) * factor),
        reattributedOutRevenue: Math.max(0, (contribution.reattributedOutRevenue ?? 0) * factor)
      }
    ])
  );

// ==== HROAS: combine two per-model breakdown maps (e.g. rolling children up to a parent node). ====
export const sumModelRevenueBreakdowns = (
  a: ModelRevenueBreakdown | undefined,
  b: ModelRevenueBreakdown | undefined
): ModelRevenueBreakdown | undefined => {
  if (!a && !b) {
    return undefined;
  }
  const merged: ModelRevenueBreakdown = { ...a };
  for (const [modelId, contribution] of Object.entries(b ?? {})) {
    const prior = merged[modelId];
    merged[modelId] = {
      directRevenue: (prior?.directRevenue ?? 0) + contribution.directRevenue,
      indirectRevenue: (prior?.indirectRevenue ?? 0) + (contribution.indirectRevenue ?? 0),
      reattributedOutRevenue:
        (prior?.reattributedOutRevenue ?? 0) + (contribution.reattributedOutRevenue ?? 0)
    };
  }
  return merged;
};

// ==== HROAS: fold one row's revenue into a per-model breakdown map. Reads row.byModel when
// present (an already-blended row from processScenarioResult) else falls back to a single entry
// keyed by row.modelId (the common, unblended case). Returns the input map unchanged when the
// row carries neither (non-HROAS rows), so this is a no-op elsewhere. ====
export const mergeRowIntoModelRevenue = (
  existing: ModelRevenueBreakdown | undefined,
  row: {
    modelId?: string;
    byModel?: ModelRevenueBreakdown;
    directRevenue: number;
    indirectRevenue?: number;
    reattributedOutRevenue?: number;
  }
): ModelRevenueBreakdown | undefined => {
  const rowByModel: ModelRevenueBreakdown | undefined =
    row.byModel ??
    (row.modelId
      ? {
          [row.modelId]: {
            directRevenue: row.directRevenue,
            indirectRevenue: row.indirectRevenue,
            reattributedOutRevenue: row.reattributedOutRevenue
          }
        }
      : undefined);
  return rowByModel ? sumModelRevenueBreakdowns(existing, rowByModel) : existing;
};

export interface ScenarioNode {
  baselineDirectRevenue: number;
  baselineDirectSpend: number;
  baselineUseHistoricalFlighting: boolean;
  children: ScenarioNode[];
  isDemandCapture?: boolean;
  name: string;
  previousDirectRevenue: number;
  previousDirectSpend: number;
  refIds?: Record<string, { directRevenue: number; directSpend: number }>;
  directRevenue: number;
  directSpend: number;
  type: string;
  useHistoricalFlighting: boolean;
  // rMMM read-only fields (populated from model output, never user-editable)
  indirectSpend?: number;
  indirectRevenue?: number;
  indirectRevenueCiLo?: number;
  indirectRevenueCiHi?: number;
  actualizedRevenueCiLo?: number;
  actualizedRevenueCiHi?: number;
  // rMMM analyzed indirect/reattributedOut values (set by updateTreeWithAnalyzedData)
  previousIndirectSpend?: number;
  previousIndirectRevenue?: number;
  previousReattributedOutSpend?: number;
  previousReattributedOutRevenue?: number;
  // rMMM baseline indirect/reattributedOut values (set by scenarioRedshiftDataToTree /
  // buildTreeRecursive — NOT updateTreeWithAnalyzedData, which only ever touches previous*)
  baselineIndirectSpend?: number;
  baselineIndirectRevenue?: number;
  baselineReattributedOutSpend?: number;
  baselineReattributedOutRevenue?: number;
  // ==== HROAS: per-model revenue breakdown shadowing baselineDirectRevenue/previousDirectRevenue
  // respectively — populated in lockstep with those fields (scenarioRedshiftDataToTree for the
  // initial baseline construction, updateTreeWithAnalyzedData for the post-analyze "previous").
  // Powers the Change Impact "by-model contribution" panel. ====
  baselineByModel?: ModelRevenueBreakdown;
  previousByModel?: ModelRevenueBreakdown;
  // ==== Pre-Apply live-edit ESTIMATE, assuming baseline ROAS holds constant as spend
  // is dragged (baselineRevenue * newSpend/baselineSpend). This is a client-side approximation,
  // never the real model output — true revenue impact is only known after a real analyze/Apply
  // run — so it's kept fully separate from directRevenue/previousDirectRevenue. Starts out equal
  // to baseline (scenarioRedshiftDataToTree) and is rescaled by applyBaselineFactorRecursive as
  // the user edits spend. ====
  currentDirectRevenue?: number;
  currentByModel?: ModelRevenueBreakdown;
}

export interface VisibleNode {
  node: ScenarioNode;
  level: number;
  key: string;
}

export const getFlightingWeights = (node: ScenarioNode): number[] | null => {
  const collectByKey = (n: ScenarioNode): Record<string, number> => {
    if (!n.children || n.children.length === 0) {
      if (!n.refIds) {
        return {};
      }
      return Object.fromEntries(Object.entries(n.refIds).map(([k, v]) => [k, v.directSpend]));
    }
    const agg: Record<string, number> = {};
    for (const child of n.children) {
      for (const [k, v] of Object.entries(collectByKey(child))) {
        agg[k] = (agg[k] ?? 0) + v;
      }
    }
    return agg;
  };

  const byKey = collectByKey(node);
  const sorted = Object.keys(byKey)
    .sort()
    .map(k => byKey[k]);
  if (sorted.length === 0) {
    return null;
  }
  const total = sorted.reduce((s, v) => s + v, 0);
  if (total === 0) {
    return null;
  }
  return sorted.map(v => v / total);
};

export interface ApplySetTotalValueArgs {
  tree: ScenarioNode[];
  nodeKey: string;
  newTotal: number;
  metric?: 'directSpend' | 'directRevenue';
}

export interface ApplyFlightingChangeArgs {
  tree: ScenarioNode[];
  nodeKey: string;
  val: boolean;
}

export interface GetVisibleNodesArgs {
  nodes: ScenarioNode[];
  expandedMap: Record<string, boolean>;
  sortOption: SORT_BY_ENUM;
  sortDirection: string;
  defaultExpandDepth?: number;
  level?: number;
  keyPrefix?: string;
}

export interface AnalyzeScenarioArgs {
  end: string;
  modelConfig: ModelConfig;
  newScenarioTree: ScenarioNode[];
  onProgress?: (progress: CometProgress) => void;
  start: string;
}

export interface AdjustScenarioArgs {
  currentScenarioTree: ScenarioNode[];
  newBaselineTree: ScenarioNode[];
  option: PLAN_OPTIONS;
}

interface SavedScenarioItem {
  directRevenue: number;
  directSpend: number;
  useHistoricalFlighting: boolean;
}
type SavedScenarioMap = Record<string, SavedScenarioItem>;

interface Metrics {
  baselineDirectRevenue: number;
  baselineDirectSpend: number;
  baselineUseHistoricalFlighting: boolean;
  isDemandCapture?: boolean;
  previousDirectRevenue: number;
  previousDirectSpend: number;
  refIds: Record<string, { directRevenue: number; directSpend: number }>;
  directRevenue: number;
  directSpend: number;
  useHistoricalFlighting: boolean;
  // rMMM — undefined for models that never emit indirect values
  baselineIndirectRevenue?: number;
  baselineIndirectSpend?: number;
  previousIndirectRevenue?: number;
  previousIndirectSpend?: number;
  baselineReattributedOutRevenue?: number;
  baselineReattributedOutSpend?: number;
  previousReattributedOutRevenue?: number;
  previousReattributedOutSpend?: number;
  // ==== HROAS: see the matching fields on ScenarioNode ====
  baselineByModel?: ModelRevenueBreakdown;
  previousByModel?: ModelRevenueBreakdown;
}

type ScenarioMetrics = Record<string, Metrics>;
type ScenarioHierarchy = Record<string, string[]>;

interface AnalyzeScenarioOutput {
  dataMap: Record<string, ScenarioDateRow>;
  pairwiseData?: PairwiseInteraction[];
  // The raw (pre-processed) lambda response — kept alongside the processed dataMap/pairwiseData
  // so callers can export it verbatim (e.g. Scenario Planning's raw-analysis JSON download).
  rawResult: AnalyzeScenarioResult;
}

type ScenarioSpendMap = Record<string, { directSpend: number; flighting: string }>;

const DEFAULT_HIERARCHY: ScenarioHierarchy = {
  channel: ['platform'],
  platform: ['tactic'],
  tactic: []
};

export { toFloat };

export const isNA = (val: string): boolean => val.toLowerCase() === 'n/a';

export const stripDcDgMarker = (s: string): string => s.replace(/::(dc|dg)$/, '');

export const capitalizeNames = (s: string): string =>
  s === undefined || s === null
    ? ''
    : s
        .split(' ')
        .map(word => {
          if (!word.length) {
            return '';
          }
          const mapped = CHANNEL_NAME_MAP[word.toLowerCase()];
          return mapped || word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');

// Snake_case identifier -> human-readable label (e.g. "purchase_intent" -> "Purchase Intent").
export const formatKpiName = (s: string): string => capitalizeNames(humanize(s));

// A run the UI must treat as view-only: never auto-selected, never passed to getModelConfig for
// its S3 keys. Driven by the backend's own artifact discovery (hasScenarioArtifacts) rather than
// output_file_version directly, so this stays correct even if that correlation ever changes.
// Always false for GeoMMM/BrandEquity, which never set hasScenarioArtifacts.
export const isLegacyRmmmRun = (model: ScenarioPlanningModelExt): boolean =>
  model.modelType === ScenarioPlanningModelType.RMMM && model.hasScenarioArtifacts === false;

// A run's stable identifier across model types — used to track the picker selection. GeoMMM has a
// single model artifact (s3Key); BrandEquity is keyed by its stage-two (revenue) model file
// because stageOneModels can bundle several brand-health metrics into one run with a single
// stage-two model. rMMM keys by its f(x,z) model file when present, falling back to its config
// file (always present — see resolveRMMMRun on the backend) for a legacy run that has no f(x,z)
// model.
export const modelRunKey = (model: ScenarioPlanningModelExt): string => {
  switch (model.modelType) {
    case ScenarioPlanningModelType.GeoMMM:
      return model.s3Key;
    case ScenarioPlanningModelType.RMMM:
      return model.fxzModelS3Key ?? model.configS3Key ?? '';
    case ScenarioPlanningModelType.BrandEquity:
      return model.stageTwoModelS3Key;
    default:
      return '';
  }
};

// Cache key for a model's fetched analyze/baseline data, stable across which order a
// multi-model type's parts were selected in (BrandEquity's stageOneModels, HROAS's models).
// The run-identifying args every HROAS lambda call takes (drops modelType from the config).
export const toHroasRunArgs = ({ cid, kpi, runId, slotIds }: HROASRunArgs): HROASRunArgs => ({
  cid,
  kpi,
  runId,
  slotIds
});

export const getModelCacheKey = (modelConfig: ModelConfig | undefined): string => {
  if (!modelConfig) {
    return '';
  }
  if (modelConfig.modelType === MODEL_TYPES.GEO_MMM) {
    return modelConfig.modelS3Key;
  }
  if (modelConfig.modelType === MODEL_TYPES.BRAND_EQUITY) {
    const stageOneKeys = modelConfig.stageOneModels
      .map(m => m.s3Key)
      .sort()
      .join(DATA_KEY_SEPERATOR);
    return `${stageOneKeys}${DATA_KEY_SEPERATOR}${modelConfig.stageTwoModelS3Key}`;
  }
  if (modelConfig.modelType === MODEL_TYPES.HROAS) {
    // Run id + sorted slot ids: switching run (or the slot set) must never serve another run's data.
    return [modelConfig.runId, ...[...modelConfig.slotIds].sort()].join(DATA_KEY_SEPERATOR);
  }
  return `${modelConfig.fxzModelS3Key}${DATA_KEY_SEPERATOR}${modelConfig.configS3Key}`;
};

// Default model selection: most-recent client-facing run, falling back to the most-recent run
// overall (only reachable by internal users, since external users never see non-client-facing
// runs). Excludes legacy rMMM runs — they're not offered in the picker, so auto-selecting one would
// load a run the user can't reselect with no explanation. Returns undefined if that leaves
// nothing (e.g. every visible rMMM run is legacy), same as the no-models case.
export const pickDefaultModel = (
  models: ScenarioPlanningModelExt[]
): ScenarioPlanningModelExt | undefined => {
  const selectable = models.filter(model => !isLegacyRmmmRun(model));
  const byDateDesc = [...selectable].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return byDateDesc.find(model => model.clientFacing) ?? byDateDesc[0];
};

const normalizeFeatureKey = (key: string): string =>
  Array.from(new Set(key.split(', ')))
    .join(', ')
    .replace(/, N\/A/, '');

// Maps a demand-capture flag to its suffix; returns '' when the flag is undefined/null.
const demandCaptureSuffix = (
  isDemandCapture: boolean | undefined,
  dcLabel: string,
  dgLabel: string
): string => {
  if (isDemandCapture === true) return dcLabel;
  if (isDemandCapture === false) return dgLabel;
  return '';
};

export const getGroupKey = (row: ScenarioDateRow, groupBy: GROUP_BY_ENUM): string => {
  const channel = capitalizeNames(row.channel);
  const platform = isNA(row.platform) ? '' : capitalizeNames(row.platform);
  const tactic = isNA(row.tactic) ? '' : capitalizeNames(row.tactic);
  const dcSuffix = demandCaptureSuffix(row.isDemandCapture, '_dc', '_dg');
  if (groupBy === GROUP_BY_ENUM.PLATFORM) {
    return `${channel}_${platform}${dcSuffix}`;
  }
  if (groupBy === GROUP_BY_ENUM.TACTIC) {
    return `${channel}_${platform}_${tactic}${dcSuffix}`;
  }
  return `${channel}${dcSuffix}`;
};

export const getChartLabel = (row: ScenarioDateRow, groupBy: GROUP_BY_ENUM): string => {
  if (groupBy === GROUP_BY_ENUM.PLATFORM) {
    return capitalizeNames(row.platform);
  }
  if (groupBy === GROUP_BY_ENUM.TACTIC) {
    return capitalizeNames(row.tactic);
  }
  return capitalizeNames(row.channel);
};

export const buildScenarioKey = (
  channel: string,
  platform: string,
  tactic: string,
  groupBy: GROUP_BY_ENUM,
  isDemandCapture?: boolean
): string => {
  const dcSuffix = demandCaptureSuffix(isDemandCapture, ', dc', ', dg');
  return (
    [
      channel,
      groupBy !== GROUP_BY_ENUM.CHANNEL ? platform : undefined,
      groupBy === GROUP_BY_ENUM.TACTIC ? tactic : undefined
    ]
      .filter(val => val !== null && val !== undefined && !isNA(val) && val !== '')
      .join(', ') + dcSuffix
  );
};

export const getScenarioKeyBase = (row: ScenarioDateRow, groupBy: GROUP_BY_ENUM): string =>
  buildScenarioKey(
    capitalizeNames(row.channel),
    capitalizeNames(row.platform),
    capitalizeNames(row.tactic),
    groupBy,
    row.isDemandCapture
  );

export const getMetricValue = (row: ScenarioDateRow, variable: SCENARIO_METRIC_ENUM): number => {
  if (variable === SCENARIO_METRIC_ENUM.ROAS) {
    return row.directSpend ? row.directRevenue / row.directSpend : 0;
  }
  if (variable === SCENARIO_METRIC_ENUM.CPX) {
    // Cost per conversion ($ / KPI). The outcome (conversion count for non-revenue models)
    // lives in directRevenue, so cost = spend / outcome.
    return row.directRevenue ? row.directSpend / row.directRevenue : 0;
  }
  if (variable === SCENARIO_METRIC_ENUM.SPEND) {
    return Number.isFinite(row.directSpend) ? row.directSpend : 0;
  }
  // REVENUE and KPI_VOLUME are the same underlying outcome number; only the unit/label
  // differ (dollars for revenue models, conversion counts for non-revenue models).
  if (variable === SCENARIO_METRIC_ENUM.REVENUE || variable === SCENARIO_METRIC_ENUM.KPI_VOLUME) {
    return Number.isFinite(row.directRevenue) ? row.directRevenue : 0;
  }
  const val = row[variable] as number;
  return Number.isFinite(val) ? val : 0;
};

// Reattribution is zero-sum: whatever a demand-generation channel gains (indirect*), a
// demand-capture channel gives up an equal amount (reattributedOut*). "Actualized" therefore nets
// the two out rather than just adding indirect — direct + indirect - reattributedOut — otherwise a
// demand-capture channel's actualized value double-counts the same dollar that was already added
// to the demand-generation channel it came from.
export const getActualizedMetricValue = (
  row: ScenarioDateRow,
  variable: SCENARIO_METRIC_ENUM
): number => {
  const direct = getMetricValue(row, variable);
  if (variable === SCENARIO_METRIC_ENUM.SPEND) {
    return direct + (row.indirectSpend ?? 0) - (row.reattributedOutSpend ?? 0);
  }
  // REVENUE and KPI_VOLUME share the same outcome number; net the indirect/reattributed-out contribution.
  if (variable === SCENARIO_METRIC_ENUM.REVENUE || variable === SCENARIO_METRIC_ENUM.KPI_VOLUME) {
    return direct + (row.indirectRevenue ?? 0) - (row.reattributedOutRevenue ?? 0);
  }
  const actualizedSpend =
    row.directSpend + (row.indirectSpend ?? 0) - (row.reattributedOutSpend ?? 0);
  const actualizedRevenue =
    row.directRevenue + (row.indirectRevenue ?? 0) - (row.reattributedOutRevenue ?? 0);
  if (variable === SCENARIO_METRIC_ENUM.ROAS) {
    return actualizedSpend ? actualizedRevenue / actualizedSpend : 0;
  }
  if (variable === SCENARIO_METRIC_ENUM.CPX) {
    return actualizedRevenue ? actualizedSpend / actualizedRevenue : 0;
  }
  return direct;
};

export const getLeafNodes = (node: ScenarioNode): ScenarioNode[] => {
  const { children } = node;
  if (!children || children.length === 0) {
    return [node];
  }
  return children.flatMap(getLeafNodes);
};

const buildKeyFromRow = (row: ScenarioDateRow, hierarchyKeys: string[]): string =>
  hierarchyKeys.map(key => row[key]).join(', ');

const findRootLevel = (hierarchy: ScenarioHierarchy): string => {
  const allValues = new Set(Object.values(hierarchy).flat());
  const root = Object.keys(hierarchy).find(key => !allValues.has(key));
  return root || Object.keys(hierarchy)[0];
};

const buildTreeRecursive = (
  scenarioMetrics: ScenarioMetrics,
  hierarchy: ScenarioHierarchy,
  currentLevel: string,
  keyPrefix: string
): ScenarioNode[] => {
  const groups: Set<string> = new Set();
  const prefix = keyPrefix ? `${keyPrefix}, ` : '';
  const currentDepth = keyPrefix.split(', ').filter(Boolean).length;

  if (!currentLevel) {
    return [];
  }

  for (const key of Object.keys(scenarioMetrics)) {
    if (key.startsWith(prefix)) {
      const parts = key.split(', ');
      if (parts.length > currentDepth) {
        groups.add(parts[currentDepth]);
      }
    }
  }

  const childLevel = hierarchy[currentLevel]?.[0];
  return Array.from(groups).map(groupName => {
    const newPrefix = keyPrefix ? `${keyPrefix}, ${groupName}` : groupName;
    const children = buildTreeRecursive(scenarioMetrics, hierarchy, childLevel, newPrefix);

    let totalBaselineDirectSpend = 0;
    let totalBaselineDirectRevenue = 0;
    let totalPreviousDirectSpend = 0;
    let totalPreviousDirectRevenue = 0;
    let totalDirectSpend = 0;
    let totalDirectRevenue = 0;
    let totalBaselineIndirectSpend = 0;
    let totalBaselineIndirectRevenue = 0;
    let totalPreviousIndirectSpend = 0;
    let totalPreviousIndirectRevenue = 0;
    let totalBaselineReattributedOutSpend = 0;
    let totalBaselineReattributedOutRevenue = 0;
    let totalPreviousReattributedOutSpend = 0;
    let totalPreviousReattributedOutRevenue = 0;
    let sawIndirect = false;
    let isDemandCapture;
    let useHistoricalFlighting = true;
    let baselineUseHistoricalFlighting = true;
    let refIds: Record<string, { directRevenue: number; directSpend: number }> = {};
    // ==== HROAS: per-model breakdown, rolled up from children or read off the leaf's aggregated
    // scenarioMetrics entry. Previously dropped entirely here — baselineByModel/previousByModel
    // were computed in scenarioMetrics (see the leafKey aggregation above) but never copied onto
    // the actual ScenarioNode tree, so every weighted "Baseline" comparison (EditScenarioModal,
    // ChangeImpactModal) silently read undefined/$0 regardless of model. currentByModel starts
    // out equal to baselineByModel — "current" only diverges once the user edits spend, see
    // applyBaselineFactorRecursive. ====
    let baselineByModel: ModelRevenueBreakdown | undefined;
    let previousByModel: ModelRevenueBreakdown | undefined;

    if (children.length > 0) {
      for (const child of children) {
        totalBaselineDirectSpend += child.baselineDirectSpend;
        totalBaselineDirectRevenue += child.baselineDirectRevenue;
        totalPreviousDirectSpend += child.previousDirectSpend;
        totalPreviousDirectRevenue += child.previousDirectRevenue;
        totalDirectSpend += child.directSpend;
        totalDirectRevenue += child.directRevenue;
        if (
          child.baselineIndirectSpend !== undefined ||
          child.baselineIndirectRevenue !== undefined ||
          child.previousIndirectSpend !== undefined ||
          child.previousIndirectRevenue !== undefined
        ) {
          sawIndirect = true;
        }
        totalBaselineIndirectSpend += child.baselineIndirectSpend ?? 0;
        totalBaselineIndirectRevenue += child.baselineIndirectRevenue ?? 0;
        totalPreviousIndirectSpend += child.previousIndirectSpend ?? 0;
        totalPreviousIndirectRevenue += child.previousIndirectRevenue ?? 0;
        totalBaselineReattributedOutSpend += child.baselineReattributedOutSpend ?? 0;
        totalBaselineReattributedOutRevenue += child.baselineReattributedOutRevenue ?? 0;
        totalPreviousReattributedOutSpend += child.previousReattributedOutSpend ?? 0;
        totalPreviousReattributedOutRevenue += child.previousReattributedOutRevenue ?? 0;
        baselineByModel = sumModelRevenueBreakdowns(baselineByModel, child.baselineByModel);
        previousByModel = sumModelRevenueBreakdowns(previousByModel, child.previousByModel);
      }
      [{ isDemandCapture }] = children;
      useHistoricalFlighting = children.every(c => c.useHistoricalFlighting);
      baselineUseHistoricalFlighting = children.every(c => c.baselineUseHistoricalFlighting);
    } else {
      const node = scenarioMetrics[newPrefix];
      if (node) {
        totalBaselineDirectSpend = node.baselineDirectSpend;
        totalBaselineDirectRevenue = node.baselineDirectRevenue;
        totalPreviousDirectSpend = node.previousDirectSpend;
        totalPreviousDirectRevenue = node.previousDirectRevenue;
        totalDirectSpend = node.directSpend;
        totalDirectRevenue = node.directRevenue;
        ({ isDemandCapture, useHistoricalFlighting, baselineUseHistoricalFlighting } = node);
        refIds = node.refIds || {};
        if (
          node.baselineIndirectSpend !== undefined ||
          node.baselineIndirectRevenue !== undefined ||
          node.previousIndirectSpend !== undefined ||
          node.previousIndirectRevenue !== undefined
        ) {
          sawIndirect = true;
        }
        totalBaselineIndirectSpend = node.baselineIndirectSpend ?? 0;
        totalBaselineIndirectRevenue = node.baselineIndirectRevenue ?? 0;
        totalPreviousIndirectSpend = node.previousIndirectSpend ?? 0;
        totalPreviousIndirectRevenue = node.previousIndirectRevenue ?? 0;
        totalBaselineReattributedOutSpend = node.baselineReattributedOutSpend ?? 0;
        totalBaselineReattributedOutRevenue = node.baselineReattributedOutRevenue ?? 0;
        totalPreviousReattributedOutSpend = node.previousReattributedOutSpend ?? 0;
        totalPreviousReattributedOutRevenue = node.previousReattributedOutRevenue ?? 0;
        baselineByModel = node.baselineByModel;
        previousByModel = node.previousByModel;
      }
    }

    return {
      baselineDirectRevenue: totalBaselineDirectRevenue,
      baselineDirectSpend: totalBaselineDirectSpend,
      baselineUseHistoricalFlighting,
      children,
      isDemandCapture,
      name: groupName,
      previousDirectRevenue: totalPreviousDirectRevenue,
      previousDirectSpend: totalPreviousDirectSpend,
      refIds: children.length === 0 ? refIds : undefined,
      directRevenue: totalDirectRevenue,
      directSpend: totalDirectSpend,
      type: currentLevel,
      useHistoricalFlighting,
      baselineIndirectRevenue: sawIndirect ? totalBaselineIndirectRevenue : undefined,
      baselineIndirectSpend: sawIndirect ? totalBaselineIndirectSpend : undefined,
      previousIndirectRevenue: sawIndirect ? totalPreviousIndirectRevenue : undefined,
      previousIndirectSpend: sawIndirect ? totalPreviousIndirectSpend : undefined,
      baselineReattributedOutRevenue: sawIndirect ? totalBaselineReattributedOutRevenue : undefined,
      baselineReattributedOutSpend: sawIndirect ? totalBaselineReattributedOutSpend : undefined,
      previousReattributedOutRevenue: sawIndirect ? totalPreviousReattributedOutRevenue : undefined,
      previousReattributedOutSpend: sawIndirect ? totalPreviousReattributedOutSpend : undefined,
      baselineByModel,
      previousByModel,
      currentDirectRevenue: totalBaselineDirectRevenue,
      currentByModel: baselineByModel
    };
  });
};

// Unified shape for HROAS warnings: backend load failures, analyze status/notes, and
// frontend-detected data gaps all render through the same list. `kind: 'note'` marks an
// informational plan note (the model ran, possibly self-corrected); anything else is a failure
// or data gap that means a model's numbers are missing.
export interface HroasDisplayWarning {
  kind?: 'failure' | 'note';
  modelId: string;
  label?: string;
  message: string;
  detail?: string;
}

// Plan notes are internal diagnostics: viewers outside the editor groups only see failures.
export const filterHroasNotesForViewer = (
  warnings: HroasDisplayWarning[],
  canViewNotes: boolean
): HroasDisplayWarning[] => (canViewNotes ? warnings : warnings.filter(w => w.kind !== 'note'));

// Turns an HROAS analyze result's modelStatus / planNotes / warnings into display notices. A failed
// model is reported as MISSING revenue, never as $0. A partial model ran, and its `reasons` are the
// same text as its planNotes, so plan notes are listed once, as notes, rather than also as a
// per-model "partly applied" failure.
export const buildHroasAnalyzeNotices = (
  result: AnalyzeScenarioResult | undefined
): HroasDisplayWarning[] => {
  if (!result) return [];
  const notices: HroasDisplayWarning[] = [];
  for (const status of result.modelStatus ?? []) {
    if (status.status !== 'failed') continue;
    notices.push({
      kind: 'failure',
      modelId: `analyze-${status.modelId}`,
      label: status.modelId,
      message: 'failed in this scenario: its revenue is missing from the totals (not zero).',
      detail: status.reasons.length > 0 ? status.reasons.join('; ') : undefined
    });
  }
  for (const warning of result.warnings ?? []) {
    const alreadyListed = (result.modelStatus ?? []).some(
      status => status.modelId === warning.modelId && status.status === 'failed'
    );
    if (alreadyListed) continue;
    notices.push({
      kind: 'failure',
      modelId: `analyze-warning-${warning.modelId}`,
      label: warning.modelId,
      message: 'failed in this scenario: its revenue is missing from the totals (not zero).',
      detail: warning.error
    });
  }
  for (const note of result.planNotes ?? []) {
    notices.push({
      kind: 'note',
      modelId: `plan-note-${note.modelId}-${note.code}`,
      label: note.modelId,
      message: note.message
    });
  }
  return notices;
};

// Model ids whose revenue is missing from an analyze result (failed instances).
export const getHroasFailedModelIds = (result: AnalyzeScenarioResult | undefined): Set<string> =>
  new Set([
    ...(result?.warnings ?? []).map(w => w.modelId),
    ...(result?.modelStatus ?? []).filter(s => s.status === 'failed').map(s => s.modelId)
  ]);

// TS port of the backend's canonicalFeatureKey (scenarioplanning/HROAS/hroasHelpers.py): one join key
// per media feature whatever each model calls it. Hyphens and runs of spaces collapse, trailing
// empty / N/A parts are dropped, and the result is case-folded, so 'Commerce Search, Amazon Search,
// Non Branded', 'commerce search,amazon search, non-branded' and '... Non Branded, N/A' share a key.
export const canonicalFeatureKey = (name: string | undefined | null): string => {
  const parts = String(name ?? '')
    .split(',')
    .map(part => part.replaceAll('-', ' ').replaceAll(/\s+/g, ' ').trim());
  while (
    parts.length > 0 &&
    ['', 'n/a', 'na', 'none'].includes(parts[parts.length - 1].toLowerCase())
  ) {
    parts.pop();
  }
  return parts.join(', ').toLowerCase();
};

// The backend stamps every HROAS row with `featureKey`; fall back to computing it from `feature`
// only when a row lacks it.
const rowFeatureKey = (row: { feature: string; featureKey?: string }): string =>
  row.featureKey ?? canonicalFeatureKey(row.feature);

// The number of HROAS revenue rows whose feature has no spend-source counterpart. computeBaselineData
// keeps these rows with zero spend; the page surfaces this count as a warning.
export const countRevenueRowsWithoutSpendSource = (
  rawBaselineData: BaselineData | undefined
): number => {
  const spendKeys = new Set((rawBaselineData?.baselineSpend?.rows ?? []).map(rowFeatureKey));
  return (rawBaselineData?.baselineRevenue?.rows ?? []).filter(
    row => !spendKeys.has(rowFeatureKey(row))
  ).length;
};

// Flatten the raw baseline spend/revenue rows into the per-week scenario rows, filtered to the
// selected start/end window. A pure helper so the React Compiler can memoize the call.
//
// HROAS invariant: spend and revenue rows are not index-aligned, so join on week|geo|featureKey.
// Spend is counted once (non-source rows carry zero), and every revenue row is kept.
export const computeBaselineData = (
  rawBaselineData: BaselineData | undefined,
  start: string | undefined,
  end: string | undefined,
  isHroas: boolean
): ScenarioDateRow[] => {
  const spendRows = rawBaselineData?.baselineSpend?.rows ?? [];
  const revenueRows = rawBaselineData?.baselineRevenue?.rows ?? [];
  if (spendRows.length === 0 || revenueRows.length === 0) {
    return [];
  }
  const data: ScenarioDateRow[] = [];
  const hroasSpendByKey = new Map<string, BaselineSpendRow>();
  const hroasMetaByFeatureKey = new Map<string, BaselineSpendRow>();
  if (isHroas) {
    for (const row of spendRows) {
      const featureKey = rowFeatureKey(row);
      hroasSpendByKey.set(`${row.week}|${row.geo}|${featureKey}`, row);
      if (!hroasMetaByFeatureKey.has(featureKey)) {
        hroasMetaByFeatureKey.set(featureKey, row);
      }
    }
  }
  const len = isHroas ? revenueRows.length : Math.min(spendRows.length, revenueRows.length);
  // Parse the window bounds once; this loop runs over every geo x week x feature row.
  const startMs = start ? new Date(start).getTime() : undefined;
  const endMs = end ? new Date(end).getTime() : undefined;
  for (let i = 0; i < len; i++) {
    const revenueRow = revenueRows[i];
    if (!revenueRow) {
      continue;
    }
    let spendRow: BaselineSpendRow = spendRows[i];
    if (isHroas) {
      const featureKey = rowFeatureKey(revenueRow);
      const matched = hroasSpendByKey.get(`${revenueRow.week}|${revenueRow.geo}|${featureKey}`);
      spendRow =
        matched && matched.modelId === revenueRow.modelId
          ? matched
          : {
              ...(hroasMetaByFeatureKey.get(featureKey) ?? revenueRow),
              week: revenueRow.week,
              modelId: revenueRow.modelId,
              modelLabel: revenueRow.modelLabel,
              modelType: revenueRow.modelType,
              directSpend: 0,
              indirectSpend: 0,
              reattributedOutSpend: 0
            };
    }
    const {
      week,
      feature,
      channel,
      platform,
      tactic,
      rawChannel,
      rawPlatform,
      rawTactic,
      directSpend,
      indirectSpend,
      reattributedOutSpend,
      isDemandCapture
    } = spendRow;
    const { directRevenue, indirectRevenue, reattributedOutRevenue } = revenueRow;
    const rowMs = new Date(week).getTime();
    if ((startMs !== undefined && startMs > rowMs) || (endMs !== undefined && endMs < rowMs)) {
      continue;
    }
    data.push(
      reclassifyBrandEquityRevenueAsIndirect(
        {
          feature,
          [ScenarioGranularityEnum.CHANNEL]: channel,
          [ScenarioGranularityEnum.PLATFORM]: platform,
          [ScenarioGranularityEnum.TACTIC]: tactic,
          // HROAS: per-instance id/label + model kind for coloring/grouping. indirectRevenue is
          // populated generically across model types (see BaselineRevenueRow).
          modelId: spendRow.modelId,
          modelLabel: spendRow.modelLabel,
          modelType: spendRow.modelType,
          rawChannel,
          rawPlatform,
          rawTactic,
          date: week,
          directRevenue: toFloat(directRevenue),
          directSpend: toFloat(directSpend),
          indirectSpend: indirectSpend != null ? toFloat(indirectSpend) : undefined,
          indirectRevenue: indirectRevenue != null ? toFloat(indirectRevenue) : undefined,
          reattributedOutSpend:
            reattributedOutSpend != null ? toFloat(reattributedOutSpend) : undefined,
          reattributedOutRevenue:
            reattributedOutRevenue != null ? toFloat(reattributedOutRevenue) : undefined,
          isDemandCapture: isHroas ? undefined : isDemandCapture
        },
        isHroas
      )
    );
  }
  return data;
};

// Lets the date-adjust callback rebuild a baseline tree from rawBaselineData
// at the target range directly — closure-captured `baselineData` is still at
// the OLD range when the callback runs.
export const sliceBaselineRows = (
  raw: BaselineData | undefined,
  start: string,
  end: string,
  isHroas = false
): ScenarioDateRow[] => {
  if (isHroas) {
    return computeBaselineData(raw, start, end, true);
  }
  const spendRows = raw?.baselineSpend?.rows ?? [];
  const revenueRows = raw?.baselineRevenue?.rows ?? [];
  if (spendRows.length === 0 || revenueRows.length === 0) {
    return [];
  }
  const data: ScenarioDateRow[] = [];
  const len = Math.min(spendRows.length, revenueRows.length);
  for (let i = 0; i < len; i++) {
    const spendRow = spendRows[i];
    const revenueRow = revenueRows[i];
    if (!revenueRow) {
      continue;
    }
    const rowDate = new Date(spendRow.week);
    if ((start && new Date(start) > rowDate) || (end && new Date(end) < rowDate)) {
      continue;
    }
    data.push({
      feature: spendRow.feature,
      [ScenarioGranularityEnum.CHANNEL]: spendRow.channel,
      [ScenarioGranularityEnum.PLATFORM]: spendRow.platform,
      [ScenarioGranularityEnum.TACTIC]: spendRow.tactic,
      rawChannel: spendRow.rawChannel,
      rawPlatform: spendRow.rawPlatform,
      rawTactic: spendRow.rawTactic,
      date: spendRow.week,
      directRevenue: toFloat(revenueRow.directRevenue),
      directSpend: toFloat(spendRow.directSpend),
      indirectSpend: spendRow.indirectSpend != null ? toFloat(spendRow.indirectSpend) : undefined,
      indirectRevenue:
        revenueRow.indirectRevenue != null ? toFloat(revenueRow.indirectRevenue) : undefined,
      reattributedOutSpend:
        spendRow.reattributedOutSpend != null ? toFloat(spendRow.reattributedOutSpend) : undefined,
      reattributedOutRevenue:
        revenueRow.reattributedOutRevenue != null
          ? toFloat(revenueRow.reattributedOutRevenue)
          : undefined,
      isDemandCapture: spendRow.isDemandCapture
    });
  }
  return data;
};

export const scenarioRedshiftDataToTree = (
  scenarioRows: ScenarioDateRow[],
  groupBy: GROUP_BY_ENUM = GROUP_BY_ENUM.CHANNEL,
  savedScenario: SavedScenarioMap | undefined = undefined
): ScenarioNode[] => {
  const getHierarchy = (): ScenarioHierarchy => {
    if (groupBy === GROUP_BY_ENUM.CHANNEL) return DEFAULT_HIERARCHY;
    if (groupBy === GROUP_BY_ENUM.PLATFORM) return { platform: ['tactic'], tactic: [] };
    return { tactic: [] };
  };
  const hierarchy = getHierarchy();

  const rootLevel = findRootLevel(hierarchy);
  const hierarchyKeys: string[] = [];
  let currentLevel: string | undefined = rootLevel;
  while (currentLevel) {
    hierarchyKeys.push(currentLevel);
    currentLevel = hierarchy[currentLevel]?.[0];
  }

  const baseMetrics: ScenarioMetrics = {};
  for (const row of scenarioRows) {
    const rawChannel = row[ScenarioGranularityEnum.CHANNEL];
    const rawPlatform = row[ScenarioGranularityEnum.PLATFORM];
    const rawTactic = row[ScenarioGranularityEnum.TACTIC];

    let effChannel = rawChannel && !isNA(rawChannel) ? rawChannel : 'Unknown Channel';
    if (row.isDemandCapture === true) {
      effChannel = `${effChannel}::dc`;
    } else if (row.isDemandCapture === false) {
      effChannel = `${effChannel}::dg`;
    }
    const effPlatform = rawPlatform && !isNA(rawPlatform) ? rawPlatform : effChannel;
    const effTactic = rawTactic && !isNA(rawTactic) ? rawTactic : effPlatform;

    const effectiveRow = {
      ...row,
      [ScenarioGranularityEnum.CHANNEL]: effChannel,
      [ScenarioGranularityEnum.PLATFORM]: effPlatform,
      [ScenarioGranularityEnum.TACTIC]: effTactic
    };

    const leafKey = buildKeyFromRow(effectiveRow, hierarchyKeys);
    // The analyze key (refIds) must always be the model's native media_channel coordinate — the
    // ONLY value the backend's scenario_spend override matches against — never a channel/platform/
    // tactic display string, even a "raw-looking" one. `row.feature` is authoritative and always
    // correct regardless of any display-formatting heuristic (rawChannel/rawPlatform/rawTactic are
    // only ever populated when a specific regex thinks the DISPLAY value still "looks like" a raw
    // feature id — a coincidental proxy, not a real guarantee, that silently breaks the moment a
    // backend starts sending humanized channel names for a previously-featid-masked client). Falls
    // back to the old rawChannel/rawPlatform/rawTactic join only for defense-in-depth, in case a
    // row somehow lacks `feature`.
    const rawId =
      row.feature ||
      [
        row.rawChannel ?? rawChannel,
        row.rawPlatform ?? rawPlatform,
        row.rawTactic ?? rawTactic
      ].join(', ');

    if (!leafKey) {
      continue;
    }

    const current = baseMetrics[leafKey] || {
      baselineDirectRevenue: 0,
      baselineDirectSpend: 0,
      baselineUseHistoricalFlighting: true,
      isDemandCapture: false,
      previousDirectRevenue: 0,
      previousDirectSpend: 0,
      refIds: {},
      directRevenue: 0,
      directSpend: 0,
      useHistoricalFlighting: true
    };

    const existingRef = current.refIds[rawId] || { directRevenue: 0, directSpend: 0 };
    const rowIndirectRevenue = row.indirectRevenue ?? 0;
    const rowIndirectSpend = row.indirectSpend ?? 0;
    const rowReattributedOutRevenue = row.reattributedOutRevenue ?? 0;
    const rowReattributedOutSpend = row.reattributedOutSpend ?? 0;

    baseMetrics[leafKey] = {
      baselineDirectRevenue: current.baselineDirectRevenue + row.directRevenue,
      baselineDirectSpend: current.baselineDirectSpend + row.directSpend,
      baselineUseHistoricalFlighting: true,
      isDemandCapture: row.isDemandCapture,
      previousDirectRevenue: current.previousDirectRevenue + row.directRevenue,
      previousDirectSpend: current.previousDirectSpend + row.directSpend,
      refIds: {
        ...current.refIds,
        [rawId]: {
          directRevenue: existingRef.directRevenue + row.directRevenue,
          directSpend: existingRef.directSpend + row.directSpend
        }
      },
      directRevenue: current.directRevenue + row.directRevenue,
      directSpend: current.directSpend + row.directSpend,
      useHistoricalFlighting: true,
      baselineIndirectRevenue: (current.baselineIndirectRevenue ?? 0) + rowIndirectRevenue,
      baselineIndirectSpend: (current.baselineIndirectSpend ?? 0) + rowIndirectSpend,
      previousIndirectRevenue: (current.previousIndirectRevenue ?? 0) + rowIndirectRevenue,
      previousIndirectSpend: (current.previousIndirectSpend ?? 0) + rowIndirectSpend,
      baselineReattributedOutRevenue:
        (current.baselineReattributedOutRevenue ?? 0) + rowReattributedOutRevenue,
      baselineReattributedOutSpend:
        (current.baselineReattributedOutSpend ?? 0) + rowReattributedOutSpend,
      previousReattributedOutRevenue:
        (current.previousReattributedOutRevenue ?? 0) + rowReattributedOutRevenue,
      previousReattributedOutSpend:
        (current.previousReattributedOutSpend ?? 0) + rowReattributedOutSpend,
      // ==== HROAS: baseline and previous start out identical (built from the same rows here);
      // updateTreeWithAnalyzedData later replaces previousByModel to reflect a real scenario run.
      baselineByModel: mergeRowIntoModelRevenue(current.baselineByModel, row),
      previousByModel: mergeRowIntoModelRevenue(current.previousByModel, row)
    };
  }

  if (savedScenario) {
    for (const key of Object.keys(baseMetrics)) {
      const metric = baseMetrics[key];
      const { refIds } = metric;
      let savedDirectRevenue = 0;
      let savedDirectSpend = 0;
      let foundSave = false;
      let savedFlighting = true;

      for (const refId of Object.keys(refIds)) {
        const savedItem = savedScenario[refId];
        if (savedItem) {
          foundSave = true;
          savedDirectRevenue += savedItem.directRevenue;
          savedDirectSpend += savedItem.directSpend;
          savedFlighting = savedItem.useHistoricalFlighting;
        }
      }

      if (foundSave) {
        baseMetrics[key].previousDirectRevenue = savedDirectRevenue;
        baseMetrics[key].previousDirectSpend = savedDirectSpend;
        baseMetrics[key].directRevenue = savedDirectRevenue;
        baseMetrics[key].directSpend = savedDirectSpend;
        baseMetrics[key].useHistoricalFlighting = savedFlighting;
      }
    }
  }

  return buildTreeRecursive(baseMetrics, hierarchy, rootLevel, '');
};

const applyBaselineFactorRecursive = (
  node: ScenarioNode,
  factor: number,
  metric: 'directSpend' | 'directRevenue'
): ScenarioNode => {
  const newNode = { ...node };
  const { baselineDirectRevenue, baselineDirectSpend, baselineByModel, children } = newNode;

  if (!children || children.length === 0) {
    if (metric === 'directSpend') {
      newNode.directSpend = Math.max(0, baselineDirectSpend * factor);
    } else {
      newNode.directRevenue = Math.max(0, baselineDirectRevenue * factor);
    }
    // ==== Constant-ROAS live-edit estimate — rescale baseline revenue/by-model by the same
    // factor just applied above, whichever metric drove it. ====
    newNode.currentDirectRevenue = Math.max(0, baselineDirectRevenue * factor);
    newNode.currentByModel = scaleModelRevenueBreakdown(baselineByModel, factor);
    return newNode;
  }

  const newChildren = children.map(c => applyBaselineFactorRecursive(c, factor, metric));
  const totals = newChildren.reduce(
    (acc, c) => ({
      directRevenue: acc.directRevenue + c.directRevenue,
      directSpend: acc.directSpend + c.directSpend,
      currentDirectRevenue: acc.currentDirectRevenue + (c.currentDirectRevenue ?? 0)
    }),
    {
      directRevenue: 0,
      directSpend: 0,
      currentDirectRevenue: 0
    }
  );

  newNode.children = newChildren;
  newNode.directRevenue = totals.directRevenue;
  newNode.directSpend = totals.directSpend;
  newNode.currentDirectRevenue = totals.currentDirectRevenue;
  newNode.currentByModel = newChildren.reduce<ModelRevenueBreakdown | undefined>(
    (acc, c) => sumModelRevenueBreakdowns(acc, c.currentByModel),
    undefined
  );
  return newNode;
};

const applyEvenDistributionRecursive = (
  node: ScenarioNode,
  totalAmount: number,
  metric: 'directSpend' | 'directRevenue'
): ScenarioNode => {
  const leafNodes = getLeafNodes(node);
  const count = leafNodes.length || 1;
  const perNodeAmount = Math.max(0, totalAmount) / count;

  const recurse = (curr: ScenarioNode): ScenarioNode => {
    const next = { ...curr };
    const { children } = next;
    if (!children || children.length === 0) {
      if (metric === 'directSpend') {
        next.directSpend = perNodeAmount;
      } else {
        next.directRevenue = perNodeAmount;
      }
      return next;
    }
    const newChildren = children.map(recurse);
    const totals = newChildren.reduce(
      (acc, c) => ({
        directRevenue: acc.directRevenue + c.directRevenue,
        directSpend: acc.directSpend + c.directSpend,
        currentDirectRevenue: acc.currentDirectRevenue + (c.currentDirectRevenue ?? 0)
      }),
      {
        directRevenue: 0,
        directSpend: 0,
        currentDirectRevenue: 0
      }
    );

    next.children = newChildren;
    next.directRevenue = totals.directRevenue;
    next.directSpend = totals.directSpend;
    // ==== No proportional basis to project revenue from here (baseline is 0 on this path by
    // construction — see applySetTotalValue) — current*/currentByModel are left as whatever the
    // (baseline-equal, likely-zero) leaves already carry, just rolled up. ====
    next.currentDirectRevenue = totals.currentDirectRevenue;
    next.currentByModel = newChildren.reduce<ModelRevenueBreakdown | undefined>(
      (acc, c) => sumModelRevenueBreakdowns(acc, c.currentByModel),
      undefined
    );
    return next;
  };

  return recurse(node);
};

const applyFlightingChangeRecursive = (node: ScenarioNode, val: boolean): ScenarioNode => {
  const newNode = { ...node, useHistoricalFlighting: val };
  const { children } = newNode;
  if (children?.length) {
    newNode.children = children.map(c => applyFlightingChangeRecursive(c, val));
  }
  return newNode;
};

const findAndModifyNodeRecursive = (
  nodes: ScenarioNode[],
  keyParts: string[],
  modifyFn: (node: ScenarioNode) => ScenarioNode
): ScenarioNode[] => {
  if (!nodes || !nodes.length || !keyParts.length) {
    return nodes;
  }
  const [currentPart, ...remaining] = keyParts;

  return nodes.map(node => {
    const { children, name } = node;
    if (name !== currentPart) {
      return node;
    }
    if (remaining.length === 0) {
      return modifyFn(node);
    }

    const newChildren = findAndModifyNodeRecursive(children, remaining, modifyFn);
    if (newChildren === children) {
      return node;
    }

    const totals = newChildren.reduce(
      (acc, c) => ({
        baselineDirectRevenue: acc.baselineDirectRevenue + c.baselineDirectRevenue,
        baselineDirectSpend: acc.baselineDirectSpend + c.baselineDirectSpend,
        previousDirectRevenue: acc.previousDirectRevenue + c.previousDirectRevenue,
        previousDirectSpend: acc.previousDirectSpend + c.previousDirectSpend,
        directRevenue: acc.directRevenue + c.directRevenue,
        directSpend: acc.directSpend + c.directSpend,
        currentDirectRevenue: acc.currentDirectRevenue + (c.currentDirectRevenue ?? 0)
      }),
      {
        baselineDirectRevenue: 0,
        baselineDirectSpend: 0,
        previousDirectRevenue: 0,
        previousDirectSpend: 0,
        directRevenue: 0,
        directSpend: 0,
        currentDirectRevenue: 0
      }
    );
    // ==== Re-roll the edited descendant's currentByModel up through every ancestor on the path
    // back to the tree root, so a live-preview total read at any level (not just the
    // directly-edited leaf) reflects the pending edit. baselineByModel/previousByModel don't
    // change on an edit, so only currentByModel needs re-rolling here. ====
    const currentByModel = newChildren.reduce<ModelRevenueBreakdown | undefined>(
      (acc, c) => sumModelRevenueBreakdowns(acc, c.currentByModel),
      undefined
    );

    return { ...node, children: newChildren, ...totals, currentByModel };
  });
};

export const applySetTotalValue = ({
  tree,
  nodeKey,
  newTotal,
  metric = 'directSpend'
}: ApplySetTotalValueArgs): ScenarioNode[] => {
  const keyParts = nodeKey.split(', ');
  const modifyFn = (node: ScenarioNode): ScenarioNode => {
    const { baselineDirectRevenue, baselineDirectSpend } = node;
    const baseline = metric === 'directSpend' ? baselineDirectSpend : baselineDirectRevenue;
    const target = Math.max(0, newTotal);

    if (baseline > 0) {
      const factor = target / baseline;
      return applyBaselineFactorRecursive(node, factor, metric);
    }

    return applyEvenDistributionRecursive(node, target, metric);
  };
  return findAndModifyNodeRecursive(tree, keyParts, modifyFn);
};

export const applyFlightingChange = ({
  tree,
  nodeKey,
  val
}: ApplyFlightingChangeArgs): ScenarioNode[] => {
  const keyParts = nodeKey.split(', ');
  return findAndModifyNodeRecursive(tree, keyParts, n => applyFlightingChangeRecursive(n, val));
};

const nodeSort = (
  nodes: ScenarioNode[],
  sortOption: SORT_BY_ENUM,
  sortDirection: string
): ScenarioNode[] => {
  const dir = sortDirection === SORT_DIRECTION_ENUM.HIGH_TO_LOW ? -1 : 1;
  return [...nodes].sort((node1, node2) => {
    if (sortOption === SORT_BY_ENUM.ALPHABETICALLY) {
      return dir * node1.name.localeCompare(node2.name);
    }
    if (sortOption === SORT_BY_ENUM.SPEND) {
      return dir * (node1.directSpend - node2.directSpend);
    }
    if (sortOption === SORT_BY_ENUM.BASELINE_SPEND) {
      return dir * (node1.baselineDirectSpend - node2.baselineDirectSpend);
    }
    if (sortOption === SORT_BY_ENUM.BASELINE_REVENUE) {
      return dir * (node1.baselineDirectRevenue - node2.baselineDirectRevenue);
    }
    if (sortOption === SORT_BY_ENUM.BASELINE_ROAS) {
      const roas1 =
        node1.baselineDirectRevenue > 0
          ? node1.baselineDirectSpend / node1.baselineDirectRevenue
          : 0;
      const roas2 =
        node2.baselineDirectRevenue > 0
          ? node2.baselineDirectSpend / node2.baselineDirectRevenue
          : 0;
      return dir * (roas1 - roas2);
    }
    return 0;
  });
};

export const getVisibleNodes = ({
  nodes,
  expandedMap,
  sortOption,
  sortDirection,
  defaultExpandDepth = 0,
  level = 0,
  keyPrefix = ''
}: GetVisibleNodesArgs): VisibleNode[] => {
  let visibleNodes: VisibleNode[] = [];
  const sortedNodes = nodeSort(nodes, sortOption, sortDirection);
  for (const node of sortedNodes) {
    const { children, name } = node;
    const nodeKey = keyPrefix ? `${keyPrefix}, ${name}` : name;
    visibleNodes.push({ node, level, key: nodeKey });
    const isExpanded = expandedMap[nodeKey] || level < defaultExpandDepth;
    if (isExpanded && children && children.length > 0) {
      const childNodes = getVisibleNodes({
        nodes: nodeSort(children, sortOption, sortDirection),
        expandedMap,
        sortOption,
        sortDirection,
        defaultExpandDepth,
        level: level + 1,
        keyPrefix: nodeKey
      });
      visibleNodes = visibleNodes.concat(childNodes);
    }
  }
  return visibleNodes;
};

const treeToSavedScenarioMap = (tree: ScenarioNode[]): SavedScenarioMap => {
  const saveMap: SavedScenarioMap = {};

  const traverse = (node: ScenarioNode) => {
    const { children, refIds, directRevenue, directSpend, useHistoricalFlighting } = node;
    if (!children || children.length === 0) {
      if (refIds) {
        const totalBaselineDirectRevenue = Object.values(refIds).reduce(
          (sum, v) => sum + v.directRevenue,
          0
        );
        const totalBaselineDirectSpend = Object.values(refIds).reduce(
          (sum, v) => sum + v.directSpend,
          0
        );
        const refIdCount = Object.keys(refIds).length || 1;

        for (const [refId, baseline] of Object.entries(refIds)) {
          const revenueWeight =
            totalBaselineDirectRevenue > 0
              ? baseline.directRevenue / totalBaselineDirectRevenue
              : 1 / refIdCount;
          const spendWeight =
            totalBaselineDirectSpend > 0
              ? baseline.directSpend / totalBaselineDirectSpend
              : 1 / refIdCount;

          saveMap[refId] = {
            directRevenue: directRevenue * revenueWeight,
            directSpend: directSpend * spendWeight,
            useHistoricalFlighting
          };
        }
      }
    } else {
      for (const child of children) {
        traverse(child);
      }
    }
  };

  for (const root of tree) {
    traverse(root);
  }
  return saveMap;
};

const buildScenarioSpend = (
  newScenarioTree: ScenarioNode[],
  start: string,
  end: string
): { scenarioSpend: ScenarioSpendMap; scenarioArg: ScenarioLambdaInput } => {
  // [rmmm-debug] Detect refId collisions across DC/DG leaves: walk tree, record
  // each leaf path that touches each refId; any refId with >1 leaf path = collision.
  const refIdOwners: Record<string, string[]> = {};
  const leafSpendByPath: Record<string, number> = {};
  const walk = (nodes: ScenarioNode[], prefix = ''): void => {
    for (const node of nodes) {
      const path = prefix ? `${prefix}, ${node.name}` : node.name;
      if (node.children?.length) {
        walk(node.children, path);
      } else if (node.refIds) {
        leafSpendByPath[path] = node.directSpend;
        for (const refId of Object.keys(node.refIds)) {
          (refIdOwners[refId] = refIdOwners[refId] || []).push(path);
        }
      }
    }
  };
  walk(newScenarioTree);
  if (isSpDebug()) {
    const collidingRefIds = Object.entries(refIdOwners)
      .filter(([, owners]) => owners.length > 1)
      .map(([refId, owners]) => ({
        refId,
        owners,
        leafSpends: owners.map(p => leafSpendByPath[p] ?? 0)
      }));

    console.warn('[rmmm-debug] buildScenarioSpend refId collisions', {
      collidingRefIds,
      totalLeaves: Object.keys(leafSpendByPath).length,
      totalRefIds: Object.keys(refIdOwners).length
    });
  }
  const newScenario = treeToSavedScenarioMap(newScenarioTree);
  const scenarioSpend: ScenarioSpendMap = {};
  const pyKeyTrace: { origKey: string; pyKey: string; directSpend: number }[] = [];
  for (const key of Object.keys(newScenario)) {
    const pyKey = normalizeFeatureKey(key);
    if (isSpDebug()) {
      pyKeyTrace.push({ origKey: key, pyKey, directSpend: newScenario[key].directSpend });
    }
    if (isNA(pyKey)) {
      continue;
    }
    scenarioSpend[pyKey] = {
      directSpend: newScenario[key].directSpend,
      flighting: newScenario[key].useHistoricalFlighting ? 'historical' : 'even'
    };
  }
  if (isSpDebug()) {
    const pyKeyCollisions = Object.entries(
      pyKeyTrace.reduce<Record<string, { origKey: string; directSpend: number }[]>>(
        (acc, { origKey, pyKey, directSpend }) => {
          (acc[pyKey] = acc[pyKey] || []).push({ origKey, directSpend });
          return acc;
        },
        {}
      )
    ).filter(([, entries]) => entries.length > 1);

    console.warn('[rmmm-debug] buildScenarioSpend pyKey collisions', {
      pyKeyCollisions,
      totalInputs: pyKeyTrace.length,
      distinctPyKeys: new Set(pyKeyTrace.map(e => e.pyKey)).size
    });
  }
  const scenarioArg = {
    start_date: start,
    end_date: end,
    scenario_spend: Object.fromEntries(
      Object.entries(scenarioSpend).map(([k, v]) => [
        k,
        { spend: v.directSpend, flighting: v.flighting }
      ])
    )
  };
  return { scenarioSpend, scenarioArg };
};

/**
 * Converts an AnalyzeResultRow into a ScenarioDateRow.
 */
const mapRowToScenarioDateRow = (
  row: AnalyzeResultRow,
  directSpend: number,
  isHroas: boolean
): ScenarioDateRow => {
  const directRevenue = toFloat(row.directRevenue);
  const directRevenueCiLo = toFloat(row.directRevenueCiLo);
  const directRevenueCiHi = toFloat(row.directRevenueCiHi);
  return reclassifyBrandEquityRevenueAsIndirect(
    {
      feature: row.feature,
      channel: row.channel,
      platform: row.platform,
      tactic: row.tactic,
      // ==== HROAS (unmigrated): carry per-instance id/label + model kind onto scenario rows
      // (enables model coloring/grouping + rMMM-only direct/indirect on the scenario side) ====
      modelId: row.modelId,
      modelLabel: row.modelLabel,
      modelType: row.modelType,
      date: '',
      directRevenue: Number.isNaN(directRevenue) ? 0 : directRevenue,
      directSpend: Number.isNaN(directSpend) ? 0 : directSpend,
      directRevenueCiLo: Number.isNaN(directRevenueCiLo)
        ? 0
        : Math.abs(directRevenueCiLo - directRevenue),
      directRevenueCiHi: Number.isNaN(directRevenueCiHi)
        ? 0
        : Math.abs(directRevenueCiHi - directRevenue),
      // rMMM extensions — API may return these as strings
      indirectSpend: row.indirectSpend != null ? toFloat(row.indirectSpend) : undefined,
      indirectRevenue: row.indirectRevenue != null ? toFloat(row.indirectRevenue) : undefined,
      indirectRevenueCiLo:
        row.indirectRevenueCiLo != null ? toFloat(row.indirectRevenueCiLo) : undefined,
      indirectRevenueCiHi:
        row.indirectRevenueCiHi != null ? toFloat(row.indirectRevenueCiHi) : undefined,
      reattributedOutSpend:
        row.reattributedOutSpend != null ? toFloat(row.reattributedOutSpend) : undefined,
      reattributedOutRevenue:
        row.reattributedOutRevenue != null ? toFloat(row.reattributedOutRevenue) : undefined,
      reattributedOutRevenueCiLo:
        row.reattributedOutRevenueCiLo != null
          ? toFloat(row.reattributedOutRevenueCiLo)
          : undefined,
      reattributedOutRevenueCiHi:
        row.reattributedOutRevenueCiHi != null
          ? toFloat(row.reattributedOutRevenueCiHi)
          : undefined,
      actualizedRevenueCiLo:
        row.actualizedRevenueCiLo != null ? toFloat(row.actualizedRevenueCiLo) : undefined,
      actualizedRevenueCiHi:
        row.actualizedRevenueCiHi != null ? toFloat(row.actualizedRevenueCiHi) : undefined,
      isDemandCapture: row.isDemandCapture
    },
    isHroas
  );
};

/**
 * Processes scenario result rows into a ScenarioDateRow map.
 */
const processScenarioResult = (
  result: AnalyzeScenarioResult,
  scenarioSpend: ScenarioSpendMap,
  isHroas: boolean
): AnalyzeScenarioOutput => {
  const dataMap: Record<string, ScenarioDateRow> = {};
  // [rmmm-debug] Track which backend feature keys matched / did not match scenarioSpend.
  const matchedFeatures: {
    feature: string;
    inputSpend: number;
    backendDirectSpend: number | undefined;
    backendIndirectSpend: number | undefined;
  }[] = [];
  const unmatchedFeatures: string[] = [];
  const featureSeenCount: Record<string, number> = {};
  if (result?.results?.rows) {
    for (const row of result.results.rows) {
      featureSeenCount[row.feature] = (featureSeenCount[row.feature] ?? 0) + 1;
      if (scenarioSpend[row.feature] === undefined || scenarioSpend[row.feature] === null) {
        unmatchedFeatures.push(row.feature);
        continue;
      }
      matchedFeatures.push({
        feature: row.feature,
        inputSpend: scenarioSpend[row.feature].directSpend,
        backendDirectSpend: row.directSpend,
        backendIndirectSpend: row.indirectSpend
      });
      const existing = dataMap[row.feature];
      const mapped = mapRowToScenarioDateRow(row, scenarioSpend[row.feature].directSpend, isHroas);
      // HROAS: more than one model instance can report the same feature. Revenue/indirect figures
      // are additive across instances; spend is one shared input per feature, not additive, and
      // existing/mapped both derive from the same scenarioSpend[feature] value, so it's not
      // summed here. A blended row no longer belongs to a single model, so the tag is cleared.
      // ==== HROAS: byModel preserves per-model attribution through the blend above (see the
      // ScenarioDateRow.byModel doc comment). Invariant: once a feature has been seen once,
      // dataMap[feature].byModel is always populated for HROAS rows (single-entry on the first
      // occurrence, so later blends only ever need to merge one fresh row in, not self-merge). ====
      dataMap[row.feature] = !existing
        ? { ...mapped, byModel: mergeRowIntoModelRevenue(undefined, mapped) }
        : {
            ...existing,
            modelId: undefined,
            modelLabel: undefined,
            byModel: mergeRowIntoModelRevenue(existing.byModel, mapped),
            directRevenue: existing.directRevenue + mapped.directRevenue,
            directRevenueCiLo: (existing.directRevenueCiLo ?? 0) + (mapped.directRevenueCiLo ?? 0),
            directRevenueCiHi: (existing.directRevenueCiHi ?? 0) + (mapped.directRevenueCiHi ?? 0),
            indirectRevenue: (existing.indirectRevenue ?? 0) + (mapped.indirectRevenue ?? 0),
            indirectRevenueCiLo:
              (existing.indirectRevenueCiLo ?? 0) + (mapped.indirectRevenueCiLo ?? 0),
            indirectRevenueCiHi:
              (existing.indirectRevenueCiHi ?? 0) + (mapped.indirectRevenueCiHi ?? 0),
            indirectSpend: (existing.indirectSpend ?? 0) + (mapped.indirectSpend ?? 0),
            reattributedOutRevenue:
              (existing.reattributedOutRevenue ?? 0) + (mapped.reattributedOutRevenue ?? 0),
            reattributedOutRevenueCiLo:
              (existing.reattributedOutRevenueCiLo ?? 0) + (mapped.reattributedOutRevenueCiLo ?? 0),
            reattributedOutRevenueCiHi:
              (existing.reattributedOutRevenueCiHi ?? 0) + (mapped.reattributedOutRevenueCiHi ?? 0),
            reattributedOutSpend:
              (existing.reattributedOutSpend ?? 0) + (mapped.reattributedOutSpend ?? 0),
            actualizedRevenueCiLo:
              (existing.actualizedRevenueCiLo ?? 0) + (mapped.actualizedRevenueCiLo ?? 0),
            actualizedRevenueCiHi:
              (existing.actualizedRevenueCiHi ?? 0) + (mapped.actualizedRevenueCiHi ?? 0)
          };
    }
  }
  if (isSpDebug()) {
    const duplicateFeatures = Object.entries(featureSeenCount).filter(([, n]) => n > 1);

    console.warn('[rmmm-debug] processScenarioResult feature match report', {
      matchedFeatures,
      unmatchedFeatures,
      duplicateFeatures
    });
  }
  const pairwiseData = result?.pairwise?.rows
    ? extractPairwiseData(result.pairwise.rows)
    : undefined;
  return { dataMap, pairwiseData, rawResult: result };
};

/**
 * Calls the appropriate model-specific lambda, then processes results
 * through the shared pipeline.
 */
export const analyzeScenario = async ({
  end,
  modelConfig,
  newScenarioTree,
  onProgress,
  start
}: AnalyzeScenarioArgs): Promise<AnalyzeScenarioOutput> => {
  const { scenarioSpend, scenarioArg } = buildScenarioSpend(newScenarioTree, start, end);
  const isHroas = modelConfig.modelType === MODEL_TYPES.HROAS;
  let result: AnalyzeScenarioResult;
  if (modelConfig.modelType === MODEL_TYPES.BRAND_EQUITY) {
    result = await analyzeBrandEquityScenarioLambda(
      {
        stageOneModels: modelConfig.stageOneModels,
        stageTwoModelS3Key: modelConfig.stageTwoModelS3Key,
        newScenario: scenarioArg
      },
      onProgress
    );
  } else if (modelConfig.modelType === MODEL_TYPES.R_MMM) {
    result = await analyzeRMMMScenarioLambda(
      {
        fxzModelS3Key: modelConfig.fxzModelS3Key,
        configS3Key: modelConfig.configS3Key,
        clientDataS3Key: modelConfig.clientDataS3Key,
        gxWorkspaceS3Key: modelConfig.gxWorkspaceS3Key,
        gxTracesS3Keys: modelConfig.gxTracesS3Keys,
        newScenario: scenarioArg
      },
      onProgress
    );
  } else if (modelConfig.modelType === MODEL_TYPES.HROAS) {
    result = await analyzeHROASScenarioLambda(
      {
        ...toHroasRunArgs(modelConfig),
        newScenario: scenarioArg
      },
      onProgress
    );
  } else {
    result = await analyzeGeoMMMScenarioLambda(
      {
        modelS3Key: modelConfig.modelS3Key,
        newScenario: scenarioArg
      },
      onProgress
    );
  }
  return processScenarioResult(result, scenarioSpend, isHroas);
};

const adjustScenarioRecursive = (
  node: ScenarioNode,
  newBaselineNode: ScenarioNode,
  option: PLAN_OPTIONS
): ScenarioNode => {
  if (option === PLAN_OPTIONS.START_FRESH || !node) {
    return { ...newBaselineNode };
  }
  const newNode = { ...newBaselineNode };
  const { children, baselineDirectSpend, baselineDirectRevenue } = newBaselineNode;
  const {
    directSpend: currentDirectSpend,
    directRevenue: currentDirectRevenue,
    useHistoricalFlighting
  } = node;
  if (option === PLAN_OPTIONS.KEEP_SPEND_AMOUNTS) {
    newNode.directSpend = currentDirectSpend;
    newNode.directRevenue = currentDirectRevenue;
    newNode.useHistoricalFlighting = useHistoricalFlighting;
  } else if (option === PLAN_OPTIONS.KEEP_PERCENT_CHANGES) {
    const oldBaselineDirectSpend = node.baselineDirectSpend;
    const oldBaselineDirectRevenue = node.baselineDirectRevenue;
    if (oldBaselineDirectSpend > 0) {
      newNode.directSpend = baselineDirectSpend * (currentDirectSpend / oldBaselineDirectSpend);
    } else {
      newNode.directSpend = currentDirectSpend;
    }
    if (oldBaselineDirectRevenue > 0) {
      newNode.directRevenue =
        baselineDirectRevenue * (currentDirectRevenue / oldBaselineDirectRevenue);
    } else {
      newNode.directRevenue = currentDirectRevenue;
    }
    newNode.useHistoricalFlighting = useHistoricalFlighting;
  }
  if (children && children.length > 0) {
    const currentChildrenMap = new Map((node.children || []).map(c => [c.name, c]));
    newNode.children = children.map(newChild =>
      adjustScenarioRecursive(
        currentChildrenMap.get(newChild.name) as ScenarioNode,
        newChild,
        option
      )
    );
    const totals = newNode.children.reduce(
      (acc, c) => ({
        directSpend: acc.directSpend + c.directSpend,
        directRevenue: acc.directRevenue + c.directRevenue
      }),
      { directSpend: 0, directRevenue: 0 }
    );
    newNode.directSpend = totals.directSpend;
    newNode.directRevenue = totals.directRevenue;
  }
  return newNode;
};

export const adjustScenario = ({
  currentScenarioTree,
  newBaselineTree,
  option
}: AdjustScenarioArgs): ScenarioNode[] => {
  const currentMap = new Map(currentScenarioTree.map(n => [n.name, n]));
  return newBaselineTree.map(newBaselineNode => {
    const currentNode = currentMap.get(newBaselineNode.name);
    return adjustScenarioRecursive(currentNode as ScenarioNode, newBaselineNode, option);
  });
};

export const updateTreeWithAnalyzedData = (
  tree: ScenarioNode[],
  analyzedData: Record<string, ScenarioDateRow>,
  _pathPrefix = ''
): ScenarioNode[] =>
  tree.map(node => {
    const newNode = { ...node };
    const { children, refIds } = newNode;
    const nodePath = _pathPrefix ? `${_pathPrefix}, ${node.name}` : node.name;
    if (children && children.length > 0) {
      newNode.children = updateTreeWithAnalyzedData(children, analyzedData, nodePath);
      const totals = newNode.children.reduce(
        (acc, child) => ({
          previousDirectRevenue: acc.previousDirectRevenue + child.previousDirectRevenue,
          previousDirectSpend: acc.previousDirectSpend + child.previousDirectSpend,
          previousIndirectRevenue:
            acc.previousIndirectRevenue + (child.previousIndirectRevenue ?? 0),
          previousIndirectSpend: acc.previousIndirectSpend + (child.previousIndirectSpend ?? 0),
          previousReattributedOutRevenue:
            acc.previousReattributedOutRevenue + (child.previousReattributedOutRevenue ?? 0),
          previousReattributedOutSpend:
            acc.previousReattributedOutSpend + (child.previousReattributedOutSpend ?? 0)
        }),
        {
          previousDirectRevenue: 0,
          previousDirectSpend: 0,
          previousIndirectRevenue: 0,
          previousIndirectSpend: 0,
          previousReattributedOutRevenue: 0,
          previousReattributedOutSpend: 0
        }
      );
      newNode.previousDirectRevenue = totals.previousDirectRevenue;
      newNode.previousDirectSpend = totals.previousDirectSpend;
      newNode.previousIndirectRevenue = totals.previousIndirectRevenue;
      newNode.previousIndirectSpend = totals.previousIndirectSpend;
      newNode.previousReattributedOutRevenue = totals.previousReattributedOutRevenue;
      newNode.previousReattributedOutSpend = totals.previousReattributedOutSpend;
      // ==== HROAS: roll children's previousByModel up to the parent, mirroring the numeric totals above ====
      newNode.previousByModel = newNode.children.reduce<ModelRevenueBreakdown | undefined>(
        (acc, child) => sumModelRevenueBreakdowns(acc, child.previousByModel),
        undefined
      );
    } else if (refIds) {
      let totalAnalyzedDirectRevenue = 0;
      let totalAnalyzedDirectSpend = 0;
      let totalAnalyzedIndirectRevenue = 0;
      let totalAnalyzedIndirectSpend = 0;
      let totalAnalyzedReattributedOutRevenue = 0;
      let totalAnalyzedReattributedOutSpend = 0;
      // ==== HROAS: per-model breakdown of this leaf's analyzed ("previous") revenue ====
      let analyzedByModel: ModelRevenueBreakdown | undefined;
      // [rmmm-debug] Per-leaf: what refIds did we try and which matched analyzedData?
      const perLeafDebug: {
        refId: string;
        pyKey: string;
        matched: boolean;
        directSpend?: number;
        indirectSpend?: number;
      }[] = [];
      for (const refId of Object.keys(refIds)) {
        const pyKey = normalizeFeatureKey(refId);
        const data = analyzedData[pyKey];
        if (data) {
          totalAnalyzedDirectRevenue += data.directRevenue;
          totalAnalyzedDirectSpend += data.directSpend;
          totalAnalyzedIndirectRevenue += data.indirectRevenue ?? 0;
          totalAnalyzedIndirectSpend += data.indirectSpend ?? 0;
          totalAnalyzedReattributedOutRevenue += data.reattributedOutRevenue ?? 0;
          totalAnalyzedReattributedOutSpend += data.reattributedOutSpend ?? 0;
          analyzedByModel = mergeRowIntoModelRevenue(analyzedByModel, data);
          perLeafDebug.push({
            refId,
            pyKey,
            matched: true,
            directSpend: data.directSpend,
            indirectSpend: data.indirectSpend
          });
        } else {
          perLeafDebug.push({ refId, pyKey, matched: false });
        }
      }
      newNode.previousDirectRevenue = totalAnalyzedDirectRevenue;
      newNode.previousDirectSpend = totalAnalyzedDirectSpend;
      newNode.previousIndirectRevenue = totalAnalyzedIndirectRevenue;
      newNode.previousIndirectSpend = totalAnalyzedIndirectSpend;
      newNode.previousReattributedOutRevenue = totalAnalyzedReattributedOutRevenue;
      newNode.previousReattributedOutSpend = totalAnalyzedReattributedOutSpend;
      newNode.previousByModel = analyzedByModel;
    }
    return newNode;
  });

// ==== HROAS: a model slot as the SP page sees it (slot id = `{taxonomy_model_type}:{model_scope}`).
// Array order is the run's slot order, which is also the stack order of the per-model revenue
// segments and fixes each model's shade (identical across bars, chips and legend). ====
export interface HroasSpModel {
  id: string;
  label: string;
}

export interface HroasRunSelection {
  cid: string;
  kpi: string;
  runId: string;
  // Per-slot id/label and model kind, used for weighting, tooltips and the by-model columns.
  slots: Slot[];
  // Run slots left out for missing Scenario Planning artifacts (reported in the warning modal).
  skippedSlots?: Slot[];
}

// ==== HROAS: one model's weighted contribution to a bar, for the per-model revenue segments.
// `direct` is the model's direct revenue; actualized = direct + indirect − reattributedOut. ====
export interface ModelSegment {
  id: string;
  label: string;
  direct: number;
  actualized: number;
}

// Segments in slot order, limited to included models and scaled by their normalized weight — the
// same weighting the bar total uses.
export const getModelSegments = (
  byModel: ModelRevenueBreakdown | undefined,
  models: HroasSpModel[],
  includedModelIds: Set<string>,
  normalizedModelWeights: Record<string, number>
): ModelSegment[] =>
  models.flatMap(model => {
    const contribution = byModel?.[model.id];
    if (!contribution || !includedModelIds.has(model.id)) {
      return [];
    }
    const weight = normalizedModelWeights[model.id] ?? 1;
    const direct = weight * (contribution.directRevenue ?? 0);
    const actualized =
      direct +
      weight * (contribution.indirectRevenue ?? 0) -
      weight * (contribution.reattributedOutRevenue ?? 0);
    return [{ id: model.id, label: model.label, direct, actualized }];
  });
